import React, { useEffect, useContext } from 'react'
import { ButtonPrimary } from '../../../../components/atoms'
import FormFieldSelect from '../../../../components/forms/FormFieldSelect'
import FormFieldText from '../../../../components/forms/FormFieldText'
import { AppContext } from '../../../../global'
import { RequestState } from '../../../types'
import * as actions from '../store/actions'
import { useTranslation } from 'react-i18next'
import './Address.css'
import Loading from '../../../../components/Loading'
import BarSuccess from '../../../../components/atoms/BarSuccess'
import BarError from '../../../../components/atoms/BarError'
import BarInfo from '../../../../components/atoms/BarInfo'

type Props = {
  accountId: string
}

function Address(props: Props) {
  const { accountId } = props
  const { state, execute } = useContext(AppContext)
  const addressSettingsState = state.settings.address
  const { t } = useTranslation(['settings_address', 'global'])

  useEffect(() => {
    execute(actions.updateAddressReset())
    execute(actions.loadCountries())
    execute(actions.loadAddress(accountId))
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  function onFieldChange(field: string, value?: string) {
    execute(actions.updateAddressReset())
    execute(actions.updateField(field, value))
  }

  function updateAddress() {
    execute(actions.updateAddress(accountId, addressSettingsState.address))
  }

  function hasError(value?: string) {
    if (!value || value.trim() === '') {
      return true
    }
  }

  function allFieldsValid() {
    if (
      addressSettingsState.address.country &&
      addressSettingsState.address.city &&
      addressSettingsState.address.city.trim() !== ''
    ) {
      return true
    } else {
      return false
    }
  }

  function updateAddressReset() {
    execute(actions.updateAddressReset())
  }

  if (
    addressSettingsState.countriesRequestState === RequestState.Loaded &&
    addressSettingsState.addressRequestState === RequestState.Loaded
  ) {
    const countries = addressSettingsState.countries.map(country => ({
      key: country.id,
      text: country.name
    }))

    return (
      <div className="padding-m">
        <div id="address-settings-container">
          <div className="spacing-bottom-s">
            <BarInfo message={t('settings_address:info')} />
          </div>

          <FormFieldText
            title={t('settings_address:organisation_name')}
            data-cy="business-name"
            value={addressSettingsState.address.businessName}
            placeholder={t('settings_address:enter_organisation_name')}
            onChange={(value: string) => onFieldChange('businessName', value)} />

          <FormFieldText
            title={t('settings_address:address_line_one')}
            data-cy="address-line-1"
            value={addressSettingsState.address.addressLine1}
            placeholder={t('settings_address:enter_address_line_one')}
            onChange={(value: string) => onFieldChange('addressLine1', value)} />

          <FormFieldText
            title={t('settings_address:address_line_two')}
            data-cy="address-line-2"
            placeholder={t('settings_address:enter_address_line_two')}
            value={addressSettingsState.address.addressLine2}
            onChange={(value: string) => onFieldChange('addressLine2', value)} />

          <FormFieldText
            title={t('settings_address:city')}
            data-cy="city"
            placeholder={t('enter_city')}
            value={addressSettingsState.address.city}
            error={hasError(addressSettingsState.address.city)}
            mandatory
            onChange={(value: string) => onFieldChange('city', value)} />

          <FormFieldText
            title={t('settings_address:region')}
            data-cy="county"
            placeholder={t('enter_region')}
            value={addressSettingsState.address.region}
            onChange={(value: string) => onFieldChange('region', value)} />

          <FormFieldText
            title={t('settings_address:postcode')}
            data-cy="postcode"
            placeholder="Enter postcode/zip"
            value={addressSettingsState.address.postcode}
            onChange={(value: string) => onFieldChange('postcode', value)} />

          <FormFieldSelect
            title={t('settings_address:country')}
            options={countries}
            data-cy="countries"
            value={addressSettingsState.address.country}
            error={hasError(addressSettingsState.address.country)}
            mandatory
            placeholder={t('settings_address:enter_country')}
            onChange={(value: string) => onFieldChange('country', value)} />

          {addressSettingsState.updateRequestState === RequestState.Error && (
            <BarError data-cy="save-message" message={t('settings_address:save_unsuccessful')} onClose={updateAddressReset} />
          )}

          {addressSettingsState.updateRequestState === RequestState.Loaded && (
            <BarSuccess data-cy="save-message" message={t('settings_address:save_successful')} onClose={updateAddressReset} />
          )}

          <ButtonPrimary data-cy="save" disabled={!allFieldsValid()} onClick={updateAddress}>{t('global:save')}</ButtonPrimary>
        </div>
      </div>
    )
  } else {
    return (
      <Loading />
    )
  }
}

export default Address
